// Shared hat GLB registry + cached loader. Both CharacterView (in-world hat
// on the live character) and JoinScreen (flat icon preview in each selector
// button) pull from the same cache, so every model is fetched + parsed once.

import * as THREE from 'three';
import { createGLTFLoader } from './glbLoader.js';

export type HatId =
  | 'top' | 'chef'
  | 'baseballCap' | 'beanie' | 'bucket' | 'cowboy' | 'firefighter'
  | 'crown' | 'graduation' | 'halo' | 'party' | 'pirate'
  | 'santa' | 'sombrero' | 'viking' | 'wizard';

/** Placement tuning for a single hat. CharacterView reads this live every
 *  frame, so mutating a config object (e.g. via the dev panel sliders)
 *  updates the worn hat instantly.
 *
 *  Offsets are in the head's *local* frame (X = right, Y = up, Z = forward),
 *  applied after the head's world rotation. */
export interface HatConfig {
  scale: number;
  offsetX: number;
  offsetY: number;
  offsetZ: number;
  yawDeg: number;
  pitchDeg: number;
  rollDeg: number;
}

export const HAT_MODEL_PATHS: Record<HatId, string> = {
  top:         '/models/hats/top+hat.glb',
  chef:        '/models/hats/chef+hat.glb',
  baseballCap: '/models/hats/baseball+cap.glb',
  beanie:      '/models/hats/beanie.glb',
  bucket:      '/models/hats/bucket+hat.glb',
  cowboy:      '/models/hats/cowboy+hat.glb',
  firefighter: '/models/hats/firefighter+helmet.glb',
  crown:       '/models/hats/gold+crown.glb',
  graduation:  '/models/hats/graduation+cap.glb',
  halo:        '/models/hats/halo+ring.glb',
  party:       '/models/hats/party+hat.glb',
  pirate:      '/models/hats/pirate+hat.glb',
  santa:       '/models/hats/santa+hat.glb',
  sombrero:    '/models/hats/sombrero.glb',
  viking:      '/models/hats/viking+helmet.glb',
  wizard:      '/models/hats/wizard+hat.glb'
};

const _cache = new Map<HatId, Promise<THREE.Group>>();

/** Returns the cached, already-neutralised hat scene for `id`. The
 *  neutralisation pass (forced metalness=0, raised roughness floor) runs
 *  exactly ONCE per HatId, on the source scene before it lands in the
 *  cache — every subsequent caller gets a scene whose materials are
 *  already in the right state. Callers should still `clone(true)` before
 *  parenting into their own scene; `clone(true)` shares materials by
 *  reference, so the one neutralisation propagates to every instance. */
export function loadHatModel(id: HatId): Promise<THREE.Group> {
  let p = _cache.get(id);
  if (!p) {
    p = new Promise<THREE.Group>((resolve, reject) => {
      createGLTFLoader().load(
        HAT_MODEL_PATHS[id],
        (gltf) => {
          // Pre-neutralise the cached source so per-clone consumers
          // (CharacterView.attachHat, JoinScreen icons) skip the
          // traverse on every hat swap.
          neutraliseHatMaterials(gltf.scene);
          resolve(gltf.scene);
        },
        undefined,
        (err) => reject(err)
      );
    });
    _cache.set(id, p);
  }
  return p;
}

/** Force GLB materials non-metallic so they don't render black without an
 *  envMap. Keeps textures / tints intact; only changes PBR knobs. */
export function neutraliseHatMaterials(root: THREE.Object3D): void {
  root.traverse((obj) => {
    const m = obj as THREE.Mesh;
    if (!(m as { isMesh?: boolean }).isMesh) return;
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    for (const mat of mats) {
      const std = mat as THREE.MeshStandardMaterial;
      if ('metalness' in std) std.metalness = 0;
      if ('roughness' in std) std.roughness = Math.max(std.roughness ?? 1, 0.7);
    }
  });
}

